import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DateTimeModule from 'react-datetime';
import { DateTime as LuxonDateTime } from 'luxon';
import { IoTrashOutline } from 'react-icons/io5';
import Modal from '@modalSrc/Modal';
import 'react-datetime/css/react-datetime.css';

const DateTime = (DateTimeModule as any)?.default || DateTimeModule;

function DateTimePicker({ field, onChange, value, autoFocus = false, openOnMount = false, dateOnly = false, pickerTitle = '', className: triggerClassName = '', displayAsText = false, ...rest }) {
    const [internalValue, setInternalValue] = useState<any>('');
    const [draftValue, setDraftValue] = useState<any>('');
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [openBaselineRawValue, setOpenBaselineRawValue] = useState('');

    const normalizeRawValue = (raw: any) => String(raw || '').trim();

    const parseUtcRawToEasternDateTime = (raw: string) => {
        const normalized = normalizeRawValue(raw);
        if (!normalized) return null;
        const easternDateTime = dateOnly
            ? LuxonDateTime.fromISO(normalized, { zone: 'America/New_York' })
            : LuxonDateTime.fromISO(normalized, { zone: 'utc' }).setZone('America/New_York');
        if (!easternDateTime.isValid) return null;
        return easternDateTime;
    };

    const parseUtcRawToEasternPickerValue = (raw: string) => {
        const easternDateTime = parseUtcRawToEasternDateTime(raw);
        if (!easternDateTime) return normalizeRawValue(raw);
        return easternDateTime.toJSDate();
    };

    const formatEasternInputToUtc = (nextValue: any) => {
        if (!nextValue || nextValue._isValid === false) return '';
        const easternDateTime =
            nextValue instanceof Date
                ? LuxonDateTime.fromJSDate(nextValue, { zone: 'America/New_York' })
                : LuxonDateTime.fromObject(
                      {
                          year: nextValue.year(),
                          month: nextValue.month() + 1,
                          day: nextValue.date(),
                          hour: nextValue.hour(),
                          minute: nextValue.minute(),
                      },
                      { zone: 'America/New_York' }
                  );
        if (!easternDateTime.isValid) return '';
        if (dateOnly) return easternDateTime.toFormat('yyyy-LL-dd');
        return easternDateTime.toUTC().toISO({ suppressMilliseconds: true }) || '';
    };

    const getDisplayValue = () => {
        const normalizedValue = normalizeRawValue(value);
        if (!normalizedValue) return dateOnly ? 'Click to set date' : 'Click to set date and time';
        const easternDateTime = parseUtcRawToEasternDateTime(normalizedValue);
        if (!easternDateTime || !easternDateTime.isValid) return normalizedValue;
        return dateOnly ? easternDateTime.toFormat('MM/dd/yyyy') : easternDateTime.toFormat('MM/dd/yyyy - hh:mm a');
    };

    const applyRawValue = (raw: string) => {
        const normalized = normalizeRawValue(raw);
        setInternalValue(normalized);
        setDraftValue(normalized);
    };

    const emitRawChange = (raw: any) => {
        const syntheticEvent = {
            target: {
                value: raw,
                getAttribute: (attr: string) => {
                    if (attr === 'data-field') return field;
                    if (attr === 'data-type') return dateOnly ? 'date' : 'datetime';
                    return null;
                },
            },
        };
        onChange(syntheticEvent);
    };

    const emitChange = (nextValue: any) => {
        if (!nextValue || nextValue._isValid === false) return;
        const formatted = formatEasternInputToUtc(nextValue);
        if (!formatted) return;
        setInternalValue(formatted);
        const syntheticEvent = {
            target: {
                value: formatted,
                getAttribute: (attr: string) => {
                    if (attr === 'data-field') return field;
                    if (attr === 'data-type') return dateOnly ? 'date' : 'datetime';
                    return null;
                },
            },
        };
        onChange(syntheticEvent);
    };

    useEffect(() => {
        applyRawValue(normalizeRawValue(value));
    }, [value]);

    useEffect(() => {
        if (openOnMount && !normalizeRawValue(value) && !isPickerOpen) {
            handleOpen();
        }
    }, [openOnMount, value, isPickerOpen]);

    // When the picker modal is open, stop Escape from propagating to the parent modal's
    // useEscapeKey listener. Registered in the capture phase so it runs first.
    useEffect(() => {
        if (!isPickerOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                handleCancel();
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [isPickerOpen]);

    const handleOpen = () => {
        const rawVal = normalizeRawValue(value) || internalValue;
        setOpenBaselineRawValue(rawVal);
        setDraftValue(parseUtcRawToEasternPickerValue(rawVal));
        setIsPickerOpen(true);
    };

    const handleCancel = () => {
        applyRawValue(openBaselineRawValue);
        emitRawChange(openBaselineRawValue);
        setIsPickerOpen(false);
    };

    const handleSave = () => {
        emitChange(draftValue);
        setIsPickerOpen(false);
    };

    const handleClear = () => {
        applyRawValue('');
        emitRawChange('');
        setIsPickerOpen(false);
    };

    const handleDateChange = (nextValue: any) => {
        if (!nextValue || nextValue._isValid === false) return;
        if (dateOnly) {
            emitChange(nextValue);
            setIsPickerOpen(false);
            return;
        }
        setDraftValue(nextValue);
    };

    const renderDay = (dayProps: any, currentDate: any) => {
        const { key, ...safeDayProps } = dayProps || {};
        const baselineDateTime = parseUtcRawToEasternDateTime(openBaselineRawValue);
        const currentDateTime = LuxonDateTime.fromObject(
            { year: currentDate.year(), month: currentDate.month() + 1, day: currentDate.date() },
            { zone: 'America/New_York' }
        );
        if (baselineDateTime && baselineDateTime.isValid && currentDateTime.hasSame(baselineDateTime, 'day')) {
            safeDayProps.className = `${safeDayProps.className || ''} rdtOriginal`.trim();
        }
        return <td key={key} {...safeDayProps}>{currentDate.date()}</td>;
    };

    const triggerClasses = ['dateTimeTextTrigger', normalizeRawValue(value) ? 'hasValue' : 'isEmpty', triggerClassName]
        .filter(Boolean)
        .join(' ')
        .trim();

    const pickerFooter = (
        <>
            <div className="footerLeft">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                <button type="button" className="btn btn-danger iconButton" onClick={handleClear} title="Clear date/time" aria-label="Clear date/time">
                    <IoTrashOutline />
                </button>
            </div>
            <div className="footerRight">
                <button type="button" className="btn btn-primary" onClick={handleSave}>Accept</button>
            </div>
        </>
    );

    return (
        <>
            <div className="dateTimePickerPopupElevated">
                <div
                    role="button"
                    tabIndex={0}
                    autoFocus={autoFocus}
                    className={triggerClasses}
                    onClick={handleOpen}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleOpen();
                        }
                    }}
                >
                    {getDisplayValue()}
                </div>
            </div>
            {createPortal(
                <Modal
                    isOpen={isPickerOpen}
                    title={pickerTitle || (dateOnly ? 'Select Date' : 'Select Date & Time')}
                    onClose={handleCancel}
                    size="sm"
                    zIndex={12001}
                    className="dateTimePickerModal"
                    footer={pickerFooter}
                >
                    <DateTime
                        className="dateTimePickerInModal"
                        value={draftValue}
                        onChange={handleDateChange}
                        open={true}
                        dateFormat="MM/DD/YYYY"
                        timeFormat={dateOnly ? false : 'HH:mm'}
                        closeOnSelect={false}
                        renderDay={renderDay}
                        renderInput={() => <></>}
                    />
                </Modal>,
                document.body
            )}
        </>
    );
}

export default DateTimePicker;

